import React, { useState, useEffect } from "react";
import { Search, X, Calendar, FileText, Phone, Tag, RotateCcw, Filter } from "lucide-react";
import { getTemplates } from "../apis/template";

const CampaignFilter = ({ 
  filters, 
  onFilterChange, 
  onApplyFilter,
  templates = [],
  whatsappAccounts = [],
  loading = false 
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    status: filters.status || "",
    campaign_name: filters.campaign_name || "",
    template_id: filters.template_id || "",
    whatsapp_account_id: filters.whatsapp_account_id || "",
    date_from: filters.date_from || "",
    date_to: filters.date_to || "",
  });

  useEffect(() => {
    setLocalFilters({
      status: filters.status || "",
      campaign_name: filters.campaign_name || "",
      template_id: filters.template_id || "",
      whatsapp_account_id: filters.whatsapp_account_id || "",
      date_from: filters.date_from || "",
      date_to: filters.date_to || "",
    });
  }, [filters]);

  const handleInputChange = (field, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilter = () => {
    onFilterChange(localFilters);
    onApplyFilter(localFilters);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      status: "",
      campaign_name: "",
      template_id: "",
      whatsapp_account_id: "",
      date_from: "",
      date_to: "",
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
    onApplyFilter(clearedFilters);
  };

  const hasActiveFilters = Object.values(localFilters).some(
    (value) => value !== "" && value !== null
  );

  const activeFilterCount = Object.values(localFilters).filter(
    (v) => v !== "" && v !== null
  ).length;

  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
    { value: "scheduled", label: "Scheduled" },
    { value: "cancelled", label: "Cancelled" },
    { value: "aborted", label: "Aborted" },
    { value: "pending", label: "Pending" },
    { value: "paused", label: "Paused" },
  ];

  return (
    <div className="mb-4">
      {/* Filter Panel - Shown when active */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-3">
          {/* Filter Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-50 rounded-lg">
                  <Search size={14} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Filter Campaigns</h3>
                  <p className="text-[10px] text-gray-600 mt-0.5">Refine your search results</p>
                </div>
                {hasActiveFilters && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                    {activeFilterCount} Active
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowFilters(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Close Filters"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Filter Body */}
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Campaign Name */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
                  <FileText size={12} className="text-gray-500" />
                  Campaign Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={localFilters.campaign_name}
                    onChange={(e) => handleInputChange("campaign_name", e.target.value)}
                    placeholder="Search by name..."
                    className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-gray-400"
                  />
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  {localFilters.campaign_name && (
                    <button
                      onClick={() => handleInputChange("campaign_name", "")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <X size={14} className="text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
                  <Tag size={12} className="text-gray-500" />
                  Status
                </label>
                <div className="relative">
                  <select
                    value={localFilters.status}
                    onChange={(e) => handleInputChange("status", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all appearance-none bg-white cursor-pointer"
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Template */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
                  <FileText size={12} className="text-gray-500" />
                  Template
                </label>
                <div className="relative">
                  <select
                    value={localFilters.template_id}
                    onChange={(e) => handleInputChange("template_id", e.target.value)}
                    disabled={loading}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all appearance-none bg-white cursor-pointer disabled:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-500"
                  >
                    <option value="">All Templates</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* WhatsApp Account */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
                  <Phone size={12} className="text-gray-500" />
                  WhatsApp Account
                </label>
                <div className="relative">
                  <select
                    value={localFilters.whatsapp_account_id}
                    onChange={(e) => handleInputChange("whatsapp_account_id", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all appearance-none bg-white cursor-pointer"
                  >
                    <option value="">All Accounts</option>
                    {whatsappAccounts.map((account) => (
                      <option key={account.id} value={account.phone_id || account.id}>
                        {account.phone_number} - {account.business_name || account.waba_name || "Business"}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Date From */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
                  <Calendar size={12} className="text-gray-500" />
                  From Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={localFilters.date_from}
                    onChange={(e) => handleInputChange("date_from", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  />
                  {localFilters.date_from && (
                    <button
                      onClick={() => handleInputChange("date_from", "")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <X size={14} className="text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>
              </div>

              {/* Date To */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
                  <Calendar size={12} className="text-gray-500" />
                  To Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={localFilters.date_to}
                    onChange={(e) => handleInputChange("date_to", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  />
                  {localFilters.date_to && (
                    <button
                      onClick={() => handleInputChange("date_to", "")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <X size={14} className="text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Filter Footer */}
          <div className="px-4 py-3 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {hasActiveFilters ? (
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                      {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
                    </span>
                    <button
                      onClick={handleClearFilters}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <RotateCcw size={12} />
                      Reset All
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No filters applied</p>
                )}
              </div>
              <button
                onClick={handleApplyFilter}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-all shadow-sm hover:shadow"
              >
                <Search size={14} />
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Toggle Button - Always Visible */}
      <button
        onClick={() => setShowFilters(!showFilters)}
        className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border transition-all shadow-sm hover:shadow ${
          hasActiveFilters
            ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300"
            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400"
        }`}
      >
        <Filter size={14} />
        <span>Filters</span>
        {hasActiveFilters && (
          <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1.5 bg-blue-600 text-white text-[10px] font-bold rounded-full">
            {activeFilterCount}
          </span>
        )}
        {!showFilters && hasActiveFilters && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClearFilters();
            }}
            className="ml-1 p-0.5 hover:bg-blue-200 rounded transition-colors"
            title="Clear all filters"
          >
            <X size={12} />
          </button>
        )}
      </button>
    </div>
  );
};

export default CampaignFilter;
